import { z } from "zod";

/**
 * My Incentives wire contract. The query is strict and empty: the person comes from the
 * session only, so a personId (or anything else) in the query is a 400. The response is every
 * year the signed-in person has a scorecard for; the page switches years in memory.
 */
export const incentivesQuerySchema = z.strictObject({});

/** Router search: a missing or invalid year means the default year. */
export const incentivesSearchSchema = z.object({
  year: z.coerce.number().int().min(2000).max(2100).optional().catch(undefined),
});

/** msMetricFormat.Enum as stored ("numberwhoel" is the warehouse's spelling). */
export type MetricFormat = string | null;

/** One scorecard line (CompScorecard), values as Incentyv stored them. Fractions: 0.5 = 50%. */
export interface IncentiveGoal {
  id: string;
  goal: string;
  /** Company, Department, Team or Role. */
  level: string | null;
  weight: number | null;
  min: number | null;
  target: number | null;
  max: number | null;
  format: MetricFormat;
  /** Year-to-date result, Incentyv's display string verbatim. */
  actual: string | null;
  /** Projected year-end result, Incentyv's display string verbatim. */
  projected: string | null;
  /** Projected achieved %; can exceed 1 (up to 2 at max). */
  achieved: number | null;
  /** Projected awarded %, as Incentyv stored it: achieved × weight, but some goals cap at their weight. */
  awarded: number | null;
  /** "All or None" metric: achieved is 0 or 1. */
  binary: boolean;
  definition: string | null;
  why: string | null;
  formula: string | null;
}

/** One reduction (PersonPeriodCompModifier), its value as stored. */
export interface IncentivePenalty {
  id: string;
  type: string;
  typeEnum: string | null;
  value: number | null;
}

export interface IncentiveYear {
  year: number;
  companyGoals: IncentiveGoal[];
  personalGoals: IncentiveGoal[];
  penalties: IncentivePenalty[];
}

export interface IncentivesView {
  /** Ascending by year; only years with an active scorecard record. */
  years: IncentiveYear[];
}
